import type { Prospect, ProspectInput } from './prospects';

/** Comparison only: never changes the URL stored on a prospect. */
export function linkedInKey(url: string): string {
  const raw = url.trim();
  if (!raw) return '';
  const address = raw.replace(/^https?:\/\//i, '');
  const hostEnd = address.search(/[/?#]/);
  const host = (hostEnd === -1 ? address : address.slice(0, hostEnd)).toLowerCase();
  const rest = hostEnd === -1 ? '' : address.slice(hostEnd);
  if (host === 'linkedin.com' || host.endsWith('.linkedin.com')) {
    const path = rest.replace(/[?#].*$/, '');
    const match = /^\/(?:mwlite\/)?in\/([^/]+)/i.exec(path);
    if (match?.[1]) {
      let slug = match[1];
      try {
        slug = decodeURIComponent(slug);
      } catch {
        // Keep malformed escapes as typed rather than crashing the preview.
      }
      return `linkedin.com/in/${slug.toLowerCase().normalize('NFC')}`;
    }
  }
  // Non-profile links retain their query and protocol: distinct ids stay distinct.
  return raw.toLowerCase().replace(/\/+$/, '');
}

function nameKey(first: string, last: string, company: string): string {
  return `${first} ${last}|${company}`.trim().toLowerCase().replace(/\s+/g, ' ');
}

type Existing = Pick<Prospect, 'linkedin_url' | 'first_name' | 'last_name' | 'company_name'>;
function possibleKey(row: Existing): string {
  return row.first_name.trim() && row.last_name.trim() && row.company_name.trim()
    ? nameKey(row.first_name, row.last_name, row.company_name)
    : '';
}

export function dedupProspectRows(
  rows: readonly ProspectInput[],
  existing: readonly Existing[],
  includePossible = false,
): { rows: ProspectInput[]; possible: ProspectInput[]; duplicates: number } {
  const urls = new Set(existing.map((row) => linkedInKey(row.linkedin_url)).filter(Boolean));
  const names = new Set(existing.map(possibleKey).filter(Boolean));
  const filtered: ProspectInput[] = [];
  const possible: ProspectInput[] = [];
  let duplicates = 0;
  for (const row of rows) {
    const key = linkedInKey(row.linkedin_url);
    if (key && urls.has(key)) {
      duplicates += 1;
      continue;
    }
    // Reserve even excluded possible rows so later URL variants cannot slip in.
    if (key) urls.add(key);
    const name = possibleKey(row);
    if (name && names.has(name)) {
      possible.push(row);
      if (includePossible) filtered.push(row);
    } else {
      filtered.push(row);
    }
  }
  return { rows: filtered, possible, duplicates };
}
